// ActivityLogReport.js — Centralized Activity / Audit log viewer.
// Reads from dbo.ModuleAuditLog via POST /api/reports/activity-log with
// rich filters: date range, module, action, user, severity, free-text.
// Each row expands to show OLD vs NEW JSON snapshots.
import React, { useEffect, useState } from 'react';
import {
  Box, Container, Card, CardContent, Typography, TextField, Button,
  Grid, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  TablePagination, Chip, Snackbar, Alert, CircularProgress, MenuItem,
  IconButton, Tooltip, Collapse,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import HistoryIcon from '@mui/icons-material/History';
import * as XLSX from 'xlsx';
import config from '../../config';

const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');

const todayStr = () => new Date().toISOString().slice(0, 10);
const weekAgo  = () => new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);

const MODULES = ['', 'Booking', 'Invoice', 'Payment', 'Restaurant', 'Spa',
                 'Laundry', 'MiniBar', 'Housekeeping', 'Identity', 'Inventory', 'API'];
const ACTIONS = ['', 'POST', 'PUT', 'PATCH', 'DELETE', 'Insert', 'Update',
                 'Cancel', 'Refund', 'StatusChange'];
const SEVERITIES = ['', 'Info', 'Warning', 'Error'];

const severityColor = (s) =>
  s === 'Error'   ? 'error'  :
  s === 'Warning' ? 'warning':
                    'success';

const prettyJson = (s) => {
  if (!s) return '';
  try { return JSON.stringify(JSON.parse(s), null, 2); }
  catch { return s; }
};

// #4: turn the raw endpoint into a human-readable action. Reads clientUrl +
// entity, so it works on EXISTING rows too — the log reads like "Created a
// booking" / "Applied a discount" instead of "POST" / "PUT".
const describeAction = (row) => {
  const url = `${row.clientUrl || ''} ${row.entityName || ''}`.toLowerCase();
  const has = (s) => url.includes(s);
  if (has('saveorder')) return 'Placed a restaurant order';
  if (has('cancelitem') || has('cancelroomitem')) return 'Cancelled an order item';
  if (has('updateitem') || has('updateroomitem')) return 'Edited an order item';
  if (has('updaterestaurantpayment')) return 'Edited a restaurant payment';
  if (has('savepayment') || has('addpayment')) return 'Added a payment';
  if (has('generateinvoice') || has('saveinvoice')) return 'Generated an invoice';
  if (has('undoinvoice')) return 'Reverted an invoice';
  if (has('checkinbooking')) return 'Checked in a guest';
  if (has('undocheckin')) return 'Undid a check-in';
  if (has('removeearlycheckincharge')) return 'Removed an early check-in charge';
  if (has('bookpackage') || has('savebooking') || has('savehotelbokking')) return 'Created a booking';
  if (has('cancelbooking')) return 'Cancelled a booking';
  if (has('updatebookingdetails') || has('updateuserdetails')) return 'Updated a booking';
  if (has('adddiscount')) return 'Applied a discount';
  if (has('cover')) return 'Added a cover charge';
  if (has('addon')) return 'Updated add-ons';
  if (has('createitem')) return 'Created a menu item';
  if (has('updaterestitem') || has('updateitemimage')) return 'Updated a menu item';
  if (has('createcategory')) return 'Created a category';
  if (has('adddepartment')) return 'Added a department';
  if (has('createuser')) return 'Created a user';
  if (has('override') || has('rbac') || has('permission')) return 'Updated permissions';
  if (has('savehotel') || has('updatehotel')) return 'Saved hotel setup';
  if (has('company')) return 'Updated company details';
  if (has('send')) return 'Sent a notification';
  const m = (row.actionType || '').toUpperCase();
  const verb = m === 'DELETE' ? 'Deleted' : m === 'PUT' || m === 'PATCH' ? 'Updated' : 'Saved';
  return `${verb} ${row.moduleName || row.entityName || 'record'}`;
};

const Row = ({ row }) => {
  const [open, setOpen] = useState(false);
  const has = row.oldValues || row.newValues;
  return (
    <>
      <TableRow hover>
        <TableCell sx={{ width: 40 }}>
          {has && (
            <IconButton size='small' onClick={() => setOpen(!open)}>
              {open ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>{new Date(row.performedAt).toLocaleString('en-IN')}</TableCell>
        <TableCell>
          <Chip size='small' label={row.severity || 'Info'} color={severityColor(row.severity)} />
        </TableCell>
        <TableCell>{row.moduleName}</TableCell>
        <TableCell>{row.entityName}{row.entityId > 0 ? ` #${row.entityId}` : ''}</TableCell>
        <TableCell>{describeAction(row)}</TableCell>
        <TableCell>{row.userName || `User #${row.performedBy || '-'}`}</TableCell>
        <TableCell><small style={{ fontFamily: 'monospace' }}>{row.ipAddress || '-'}</small></TableCell>
        <TableCell sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <Tooltip title={row.clientUrl || ''}>
            <span>{row.clientUrl || '-'}</span>
          </Tooltip>
        </TableCell>
      </TableRow>
      {has && (
        <TableRow>
          <TableCell colSpan={9} sx={{ p: 0, borderBottom: 'none' }}>
            <Collapse in={open} timeout='auto' unmountOnExit>
              <Box sx={{ p: 2, bgcolor: '#fafafa', display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {row.oldValues && (
                  <Box flex={1} minWidth={280}>
                    <Typography variant='caption' fontWeight='700' color='text.secondary'>OLD</Typography>
                    <pre style={{
                      margin: 0, padding: 8, background: '#fff5f5',
                      border: '1px solid #ffd6d6', borderRadius: 4,
                      fontSize: 11, overflow: 'auto', maxHeight: 240,
                    }}>{prettyJson(row.oldValues)}</pre>
                  </Box>
                )}
                {row.newValues && (
                  <Box flex={1} minWidth={280}>
                    <Typography variant='caption' fontWeight='700' color='text.secondary'>NEW</Typography>
                    <pre style={{
                      margin: 0, padding: 8, background: '#f0f7ff',
                      border: '1px solid #cfe0ff', borderRadius: 4,
                      fontSize: 11, overflow: 'auto', maxHeight: 240,
                    }}>{prettyJson(row.newValues)}</pre>
                  </Box>
                )}
                {row.userAgent && (
                  <Box width='100%'>
                    <Typography variant='caption' color='text.secondary'>
                      <strong>User-Agent:</strong> {row.userAgent}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default function ActivityLogReport() {
  const [filters, setFilters] = useState({
    fromDate: weekAgo(),
    toDate:   todayStr(),
    module:   '',
    actionType: '',
    severity: '',
    userId:   '',
    search:   '',
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ items: [], totalCount: 0 });
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const notify = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  const load = async () => {
    if (!company?.id) return;
    setLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      const body = {
        companyId:  company.id,
        fromDate:   filters.fromDate,
        toDate:     filters.toDate,
        module:     filters.module || null,
        actionType: filters.actionType || null,
        severity:   filters.severity || null,
        userId:     filters.userId ? parseInt(filters.userId, 10) : null,
        search:     filters.search || null,
        page:       page + 1,
        pageSize:   rowsPerPage,
      };
      const res = await fetch(`${config.API_BASE_URL}/reports/activity-log`, {
        method:  'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization:  `Bearer ${token}`,
        },
        body:    JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await res.text());
      setData(await res.json());
    } catch (e) {
      console.error(e);
      notify('Failed to load activity log', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, rowsPerPage]);

  const exportExcel = () => {
    const rows = [[
      'Time', 'Severity', 'Module', 'Entity', 'Action',
      'User', 'IP', 'URL', 'Old', 'New',
    ]];
    (data.items || []).forEach((r) =>
      rows.push([
        new Date(r.performedAt).toLocaleString('en-IN'),
        r.severity, r.moduleName,
        `${r.entityName}${r.entityId > 0 ? ' #' + r.entityId : ''}`,
        describeAction(r), r.userName || r.performedBy,
        r.ipAddress || '', r.clientUrl || '',
        r.oldValues || '', r.newValues || '',
      ]),
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), 'Activity Log');
    XLSX.writeFile(wb, `ActivityLog_${filters.fromDate}_to_${filters.toDate}.xlsx`);
  };

  return (
    <Container maxWidth='xl' sx={{ py: 3 }}>
      <Box display='flex' alignItems='center' justifyContent='space-between' mb={2} flexWrap='wrap' gap={1}>
        <Box display='flex' alignItems='center' gap={1}>
          <HistoryIcon color='primary' />
          <Typography variant='h5' fontWeight='700'>Activity Log</Typography>
        </Box>
        <Box display='flex' gap={1}>
          <Button variant='outlined' startIcon={<DownloadIcon />} onClick={exportExcel} disabled={!data.items?.length}>
            Excel
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3} md={2}>
              <TextField fullWidth size='small' type='date' label='From'
                InputLabelProps={{ shrink: true }} value={filters.fromDate}
                onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })} />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField fullWidth size='small' type='date' label='To'
                InputLabelProps={{ shrink: true }} value={filters.toDate}
                onChange={(e) => setFilters({ ...filters, toDate: e.target.value })} />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField select fullWidth size='small' label='Module' value={filters.module}
                onChange={(e) => setFilters({ ...filters, module: e.target.value })}>
                {MODULES.map((m) => <MenuItem key={m || 'all'} value={m}>{m || 'All'}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField select fullWidth size='small' label='Action' value={filters.actionType}
                onChange={(e) => setFilters({ ...filters, actionType: e.target.value })}>
                {ACTIONS.map((a) => <MenuItem key={a || 'all'} value={a}>{a || 'All'}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField select fullWidth size='small' label='Severity' value={filters.severity}
                onChange={(e) => setFilters({ ...filters, severity: e.target.value })}>
                {SEVERITIES.map((s) => <MenuItem key={s || 'all'} value={s}>{s || 'All'}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField fullWidth size='small' label='User ID' value={filters.userId}
                onChange={(e) => setFilters({ ...filters, userId: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={9} md={10}>
              <TextField fullWidth size='small' label='Search (entity / URL / IP)' value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && (setPage(0), load())} />
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <Button fullWidth variant='contained' startIcon={<RefreshIcon />}
                onClick={() => { setPage(0); load(); }} disabled={loading}>
                Apply
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Result table */}
      <TableContainer component={Card}>
        <Table size='small' stickyHeader>
          <TableHead>
            <TableRow sx={{ '& th': { bgcolor: '#f5f5f5', fontWeight: 700 } }}>
              <TableCell />
              <TableCell>Time</TableCell>
              <TableCell>Severity</TableCell>
              <TableCell>Module</TableCell>
              <TableCell>Entity</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>User</TableCell>
              <TableCell>IP</TableCell>
              <TableCell>URL</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={9} align='center'><CircularProgress size={24} /></TableCell></TableRow>
            )}
            {!loading && (data.items || []).length === 0 && (
              <TableRow><TableCell colSpan={9} align='center'>No matching entries.</TableCell></TableRow>
            )}
            {!loading && (data.items || []).map((r) => <Row key={r.auditId} row={r} />)}
          </TableBody>
        </Table>
        <TablePagination
          component='div'
          count={data.totalCount || 0}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100, 200]} />
      </TableContainer>

      <Snackbar open={snack.open} autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity={snack.sev}>{snack.msg}</Alert>
      </Snackbar>
    </Container>
  );
}
